import type { DomainCategory, MockupCollection, TemplateType } from '../types';
import { API_BASE_URL, resolvePublicAssetUrl } from './api';

type CollectionQueryType = TemplateType | 'MOTION';

function getConcreteCollectionTemplateType(templateType: CollectionQueryType): TemplateType {
  return templateType === 'MOTION' ? 'MOTION-CG' : templateType;
}

function getDefaultCollectionIdForType(templateType: CollectionQueryType): string {
  const concreteType = getConcreteCollectionTemplateType(templateType);
  if (concreteType === 'AI') {
    return 'default-ai';
  }
  if (concreteType === 'MOTION-AI') {
    return 'default-motion-ai';
  }
  if (concreteType === 'MOTION-CG') {
    return 'default-motion-cg';
  }
  return 'default-cg';
}

function createDefaultCollection(templateType: CollectionQueryType): MockupCollection {
  const concreteType = getConcreteCollectionTemplateType(templateType);
  return {
    id: getDefaultCollectionIdForType(templateType),
    templateType: concreteType,
    name: 'General',
    order: 0,
    published: true,
    background: '',
    fontColor: '#ffffff'
  };
}

export async function loadCollections(
  apiBaseUrlOverride?: string,
  templateType: CollectionQueryType = 'CG'
): Promise<MockupCollection[]> {
  const apiBase = (apiBaseUrlOverride || API_BASE_URL).trim().replace(/\/$/, '');
  const defaultCollection = createDefaultCollection(templateType);
  if (!apiBase) {
    return [defaultCollection];
  }

  const url = `${apiBase}/mockups/collections.json?type=${encodeURIComponent(templateType)}`;
  try {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Collections request failed: ${response.status}`);
    }

    const payload = (await response.json()) as unknown;
    const parsed = parseCollectionsPayload(payload, url);
    return parsed.length > 0 ? ensureDefaultCollection(parsed, templateType) : [defaultCollection];
  } catch {
    return [defaultCollection];
  }
}

export function parseCollectionsPayload(payload: unknown, baseUrl: string): MockupCollection[] {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .map((item, index) => normalizeCollection(item, baseUrl, index))
    .filter((item): item is MockupCollection => item !== null)
    .sort((a, b) => a.order - b.order);
}

function normalizeCollection(value: unknown, baseUrl: string, fallbackOrder: number): MockupCollection | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const raw = value as Record<string, unknown>;
  const id = toString(raw.id) || toSlug(toString(raw.name));
  const name = toString(raw.name);
  if (!id || !name) {
    return null;
  }

  return {
    id,
    templateType: normalizeTemplateTypeField(raw.templateType),
    name,
    order: toOrder(raw.order, fallbackOrder),
    published: raw.published !== false,
    domainCategory: normalizeDomainCategory(raw.domainCategory),
    background: resolveAssetUrl(raw.background, baseUrl) || '',
    fontColor: normalizeColor(raw.fontColor)
  };
}

function normalizeDomainCategory(value: unknown): DomainCategory | undefined {
  if (value === 'App' || value === 'Promo' || value === 'Brand') {
    return value;
  }
  return undefined;
}


function normalizeTemplateTypeField(value: unknown): TemplateType {
  const normalized = String(value || '').trim().toUpperCase().replace(/_/g, '-');
  if (normalized === 'MOTION-AI' || normalized === 'MOTIONAI') {
    return 'MOTION-AI';
  }
  if (normalized === 'MOTION-CG' || normalized === 'MOTIONCG' || normalized === 'MOTION') {
    return 'MOTION-CG';
  }
  if (normalized === 'AI') {
    return 'AI';
  }
  return 'CG';
}

function ensureDefaultCollection(collections: MockupCollection[], templateType: CollectionQueryType): MockupCollection[] {
  const defaultCollection = createDefaultCollection(templateType);
  if (collections.some((item) => item.id === defaultCollection.id)) {
    return collections.slice().sort((a, b) => a.order - b.order);
  }
  return [defaultCollection, ...collections].map((item, index) => ({ ...item, order: index }));
}

function resolveAssetUrl(value: unknown, baseUrl: string): string | null {
  const input = toString(value);
  if (!input) {
    return null;
  }

  try {
    return resolvePublicAssetUrl(input, baseUrl).toString();
  } catch {
    return null;
  }
}

function normalizeColor(value: unknown): string {
  const input = toString(value);
  if (/^#[0-9a-fA-F]{6}$/.test(input)) {
    return input.toLowerCase();
  }
  return '#ffffff';
}

function toString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toOrder(value: unknown, fallback: number): number {
  const order = Number(value);
  if (Number.isFinite(order)) {
    return Math.max(0, Math.floor(order));
  }
  return fallback;
}
